import React from "react";
import { Formik } from "formik";

import {
  ActionRow,
  FieldGroup,
  FieldLabel,
  FormGrid,
  FormStack,
  PrimaryButton,
  SecondaryButton,
  SelectInput,
  TextInput,
  TextareaInput,
  ValidationErrorText,
  WideField,
} from "components/admin/forms/styles/admin-forms.styles";

export type NewsArticleFormValues = {
  title: string;
  excerpt: string;
  content: string;
  image_url: string;
  published_at: string;
  author: string;
  source_name: string;
  source_url: string;
  external_url: string;
  sport_id: string;
  team_id: string;
  player_id: string;
};

const isValidHttpUrl = (value?: string) => {
  if (!value?.trim()) {
    return true;
  }

  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (_error) {
    return false;
  }
};

export const NewsArticleForm: React.FC<{
  initialValues: NewsArticleFormValues;
  isSubmitting?: boolean;
  onCancel: () => void;
  onSubmit: (values: NewsArticleFormValues) => Promise<void> | void;
  playerOptions: Array<{ label: string; value: string | number }>;
  sportOptions: Array<{ label: string; value: string | number }>;
  teamOptions: Array<{ label: string; value: string | number }>;
  submitLabel?: string;
}> = ({
  initialValues,
  isSubmitting = false,
  onCancel,
  onSubmit,
  playerOptions,
  sportOptions,
  teamOptions,
  submitLabel,
}) => (
  <Formik<NewsArticleFormValues>
    enableReinitialize
    initialValues={initialValues}
    validate={(values) => {
      const errors: Partial<Record<keyof NewsArticleFormValues, string>> = {};

      if (!values.title.trim()) {
        errors.title = "Title is required.";
      }

      if (!values.source_name.trim()) {
        errors.source_name = "Source name is required.";
      }

      if (!values.external_url.trim()) {
        errors.external_url = "Article URL is required.";
      } else if (!isValidHttpUrl(values.external_url)) {
        errors.external_url = "Enter a valid article URL.";
      }

      if (values.source_url.trim() && !isValidHttpUrl(values.source_url)) {
        errors.source_url = "Enter a valid source URL.";
      }

      if (values.image_url.trim() && !isValidHttpUrl(values.image_url)) {
        errors.image_url = "Enter a valid image URL.";
      }

      if (values.published_at.trim() && Number.isNaN(new Date(values.published_at).getTime())) {
        errors.published_at = "Enter a valid publish date.";
      }

      return errors;
    }}
    onSubmit={async (values) => {
      await Promise.resolve(
        onSubmit({
          ...values,
          title: values.title.trim(),
          excerpt: values.excerpt.trim(),
          content: values.content.trim(),
          image_url: values.image_url.trim(),
          published_at: values.published_at.trim(),
          author: values.author.trim(),
          source_name: values.source_name.trim(),
          source_url: values.source_url.trim(),
          external_url: values.external_url.trim(),
        }),
      );
    }}
  >
    {({
      errors,
      handleSubmit,
      isSubmitting: formikSubmitting,
      submitCount,
      touched,
      values,
      setFieldValue,
      setFieldTouched,
    }) => {
      const getError = (fieldName: keyof NewsArticleFormValues) =>
        errors[fieldName] && (touched[fieldName] || submitCount > 0) ? errors[fieldName] : undefined;

      return (
        <form onSubmit={handleSubmit}>
          <FormStack>
            <FormGrid>
              <WideField>
                <FieldGroup>
                  <FieldLabel htmlFor="news-title">Title</FieldLabel>
                  <TextInput
                    aria-invalid={Boolean(getError("title"))}
                    id="news-title"
                    onBlur={() => setFieldTouched("title", true, false)}
                    onChange={(event) => setFieldValue("title", event.target.value)}
                    value={values.title}
                  />
                  {getError("title") ? <ValidationErrorText>{getError("title")}</ValidationErrorText> : null}
                </FieldGroup>
              </WideField>

              <WideField>
                <FieldGroup>
                  <FieldLabel htmlFor="news-excerpt">Excerpt</FieldLabel>
                  <TextareaInput
                    id="news-excerpt"
                    onBlur={() => setFieldTouched("excerpt", true, false)}
                    onChange={(event) => setFieldValue("excerpt", event.target.value)}
                    value={values.excerpt}
                  />
                </FieldGroup>
              </WideField>

              <WideField>
                <FieldGroup>
                  <FieldLabel htmlFor="news-content">Content</FieldLabel>
                  <TextareaInput
                    id="news-content"
                    onBlur={() => setFieldTouched("content", true, false)}
                    onChange={(event) => setFieldValue("content", event.target.value)}
                    value={values.content}
                  />
                </FieldGroup>
              </WideField>

              <FieldGroup>
                <FieldLabel htmlFor="news-source-name">Source name</FieldLabel>
                <TextInput
                  aria-invalid={Boolean(getError("source_name"))}
                  id="news-source-name"
                  onBlur={() => setFieldTouched("source_name", true, false)}
                  onChange={(event) => setFieldValue("source_name", event.target.value)}
                  value={values.source_name}
                />
                {getError("source_name") ? <ValidationErrorText>{getError("source_name")}</ValidationErrorText> : null}
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor="news-author">Author</FieldLabel>
                <TextInput
                  id="news-author"
                  onBlur={() => setFieldTouched("author", true, false)}
                  onChange={(event) => setFieldValue("author", event.target.value)}
                  value={values.author}
                />
              </FieldGroup>

              <WideField>
                <FieldGroup>
                  <FieldLabel htmlFor="news-source-url">Source URL</FieldLabel>
                  <TextInput
                    aria-invalid={Boolean(getError("source_url"))}
                    id="news-source-url"
                    onBlur={() => setFieldTouched("source_url", true, false)}
                    onChange={(event) => setFieldValue("source_url", event.target.value)}
                    value={values.source_url}
                  />
                  {getError("source_url") ? <ValidationErrorText>{getError("source_url")}</ValidationErrorText> : null}
                </FieldGroup>
              </WideField>

              <WideField>
                <FieldGroup>
                  <FieldLabel htmlFor="news-external-url">Article URL</FieldLabel>
                  <TextInput
                    aria-invalid={Boolean(getError("external_url"))}
                    id="news-external-url"
                    onBlur={() => setFieldTouched("external_url", true, false)}
                    onChange={(event) => setFieldValue("external_url", event.target.value)}
                    value={values.external_url}
                  />
                  {getError("external_url") ? <ValidationErrorText>{getError("external_url")}</ValidationErrorText> : null}
                </FieldGroup>
              </WideField>

              <WideField>
                <FieldGroup>
                  <FieldLabel htmlFor="news-image-url">Image URL</FieldLabel>
                  <TextInput
                    aria-invalid={Boolean(getError("image_url"))}
                    id="news-image-url"
                    onBlur={() => setFieldTouched("image_url", true, false)}
                    onChange={(event) => setFieldValue("image_url", event.target.value)}
                    value={values.image_url}
                  />
                  {getError("image_url") ? <ValidationErrorText>{getError("image_url")}</ValidationErrorText> : null}
                </FieldGroup>
              </WideField>

              <FieldGroup>
                <FieldLabel htmlFor="news-published-at">Published at</FieldLabel>
                <TextInput
                  aria-invalid={Boolean(getError("published_at"))}
                  id="news-published-at"
                  onBlur={() => setFieldTouched("published_at", true, false)}
                  onChange={(event) => setFieldValue("published_at", event.target.value)}
                  placeholder="2026-04-16T10:30:00Z"
                  value={values.published_at}
                />
                {getError("published_at") ? <ValidationErrorText>{getError("published_at")}</ValidationErrorText> : null}
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor="news-sport-id">Sport</FieldLabel>
                <SelectInput
                  id="news-sport-id"
                  onBlur={() => setFieldTouched("sport_id", true, false)}
                  onChange={(event) => setFieldValue("sport_id", event.target.value)}
                  value={values.sport_id}
                >
                  <option value="">No sport</option>
                  {sportOptions.map((option) => (
                    <option key={String(option.value)} value={String(option.value)}>
                      {option.label}
                    </option>
                  ))}
                </SelectInput>
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor="news-team-id">Team</FieldLabel>
                <SelectInput
                  id="news-team-id"
                  onBlur={() => setFieldTouched("team_id", true, false)}
                  onChange={(event) => setFieldValue("team_id", event.target.value)}
                  value={values.team_id}
                >
                  <option value="">No team</option>
                  {teamOptions.map((option) => (
                    <option key={String(option.value)} value={String(option.value)}>
                      {option.label}
                    </option>
                  ))}
                </SelectInput>
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor="news-player-id">Athlete</FieldLabel>
                <SelectInput
                  id="news-player-id"
                  onBlur={() => setFieldTouched("player_id", true, false)}
                  onChange={(event) => setFieldValue("player_id", event.target.value)}
                  value={values.player_id}
                >
                  <option value="">No athlete</option>
                  {playerOptions.map((option) => (
                    <option key={String(option.value)} value={String(option.value)}>
                      {option.label}
                    </option>
                  ))}
                </SelectInput>
              </FieldGroup>
            </FormGrid>

            <ActionRow>
              <SecondaryButton onClick={onCancel} type="button">
                Cancel
              </SecondaryButton>
              <PrimaryButton disabled={isSubmitting || formikSubmitting} type="submit">
                {isSubmitting || formikSubmitting ? "Saving..." : submitLabel || "Save article"}
              </PrimaryButton>
            </ActionRow>
          </FormStack>
        </form>
      );
    }}
  </Formik>
);

export default NewsArticleForm;
